import {Link} from 'react-router-dom';
import {format} from 'date-fns';
import {
  Package,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  ChevronRight,
  FileText,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {trpc, RouterOutput} from '@common-web/lib/trpc';

type Batch = RouterOutput['internal']['invoice']['listGenerationBatches']['batches'][number];

type GenerationRunFilters = {targetMonth: string};

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy h:mm a');
  } catch {
    return 'N/A';
  }
};

function getBatchStatusBadge(batch: Batch) {
  if (batch.errorCount > 0 && batch.errorCount === batch.itemCount) {
    return (
      <Badge className='bg-red-600 text-white border-0 font-medium'>
        <XCircle className='h-3 w-3 mr-1' />
        Failed
      </Badge>
    );
  }
  if (batch.withKeysCount === 0 && batch.submittedCount === 0) {
    return (
      <Badge className='bg-brand-gray text-white border-0 font-medium'>
        <Clock className='h-3 w-3 mr-1' />
        Pending
      </Badge>
    );
  }
  if (batch.errorCount > 0 || batch.withKeysCount < batch.itemCount) {
    return (
      <Badge className='bg-amber text-white border-0 font-medium'>
        <AlertTriangle className='h-3 w-3 mr-1' />
        Partial
      </Badge>
    );
  }
  return (
    <Badge className='bg-emerald text-white border-0 font-medium'>
      <CheckCircle className='h-3 w-3 mr-1' />
      Submitted
    </Badge>
  );
}

export function GenerationRuns() {
  const table = useDataTable<GenerationRunFilters>({
    defaultPageSize: 20,
    defaultFilters: {targetMonth: ''},
    syncToUrl: 'gen',
  });

  const {data: monthsData} = trpc.internal.invoice.getGenerationMonths.useQuery();

  const {data, isLoading, error} = trpc.internal.invoice.listGenerationBatches.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    targetMonth: table.filterValues.targetMonth || undefined,
  });

  const columns: Column<Batch>[] = [
    {
      id: 'targetMonth',
      header: 'Target Month',
      render: (batch) => (
        <Link
          to={`/invoices/generation/${batch.id}`}
          className='font-medium text-brand-blue hover:text-brand-blue-dark hover:underline'
        >
          {batch.targetMonth}
        </Link>
      ),
    },
    {
      id: 'invoiceDate',
      header: 'Invoice Date',
      render: (batch) => (
        <div className='flex items-center gap-2'>
          <Calendar className='h-4 w-4 text-brand-gray' />
          <span className='text-sm'>{formatDate(batch.invoiceDate)}</span>
        </div>
      ),
    },
    {
      id: 'itemCount',
      header: 'Items',
      render: (batch) => (
        <span className='font-mono font-medium text-brand-navy'>{batch.itemCount}</span>
      ),
    },
    {
      id: 'submitted',
      header: 'Submitted',
      render: (batch) => (
        <span className='font-mono text-sm'>
          {batch.withKeysCount}/{batch.itemCount}
        </span>
      ),
    },
    {
      id: 'errors',
      header: 'Errors',
      render: (batch) =>
        batch.errorCount > 0 ? (
          <Badge className='bg-red-600/10 text-red-600 border-red-600/20 text-xs font-medium'>
            {batch.errorCount}
          </Badge>
        ) : (
          <span className='text-sm text-brand-gray'>0</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (batch) => getBatchStatusBadge(batch),
    },
    {
      id: 'note',
      header: 'Note',
      render: (batch) => (
        <span className='text-sm text-brand-gray truncate max-w-[200px] block'>
          {batch.note || '-'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      render: (batch) => (
        <span className='text-sm text-brand-gray'>{formatDateTime(batch.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (batch) => (
        <Link to={`/invoices/generation/${batch.id}`}>
          <Button
            variant='outline'
            size='sm'
            className='border-brand-blue/20 text-brand-blue hover:bg-brand-blue/5'
          >
            Details
            <ChevronRight className='h-4 w-4 ml-1' />
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Summary Cards */}
      {data && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                Total Batches
                <FileText className='h-5 w-5 text-brand-blue' />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-brand-navy'>{data.totalCount}</div>
              <p className='text-xs text-brand-gray mt-1'>Generation runs</p>
            </CardContent>
          </Card>

          <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                Total Items (This Page)
                <Package className='h-5 w-5 text-emerald' />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-brand-navy'>
                {data.batches.reduce((sum, b) => sum + b.itemCount, 0)}
              </div>
              <p className='text-xs text-brand-gray mt-1'>Invoice requests</p>
            </CardContent>
          </Card>

          <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                Errors (This Page)
                <AlertTriangle className='h-5 w-5 text-red-500' />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-red-600'>
                {data.batches.reduce((sum, b) => sum + b.errorCount, 0)}
              </div>
              <p className='text-xs text-brand-gray mt-1'>Failed items</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generation Runs Table */}
      <DataTable<Batch, GenerationRunFilters>
        table={table}
        columns={columns}
        data={data?.batches}
        total={data?.totalCount}
        totalPages={data?.totalPages}
        loading={isLoading}
        error={error}
        emptyMessage='No generation runs found'
        title='Generation Runs'
        entity={{singular: 'batch', plural: 'batches'}}
        filters={[
          {
            key: 'targetMonth',
            label: 'Months',
            type: 'select',
            placeholder: 'All Months',
            options: (monthsData?.months ?? []).map((month) => ({value: month, label: month})),
          },
        ]}
        rowKey={(batch) => batch.id}
      />
    </div>
  );
}
